"use client";
import { logoContainerStyles, searchInputStyles } from "@components/layouts/navigation/styles";
import { SEARCH_OVERLAY, SEARCHBAR_INPUT } from "@e2e/helpers/homepage/constants";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Input } from "@ui/components/forms/input/Input";
import { InputLeftElement } from "@ui/components/forms/input/InputElement";
import { InputGroup } from "@ui/components/forms/input/InputGroup";
import type { BoxProps } from "@ui/components/layout/box/Box";
import { Box } from "@ui/components/layout/box/Box";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { useBreakpoint } from "@ui/hooks/useBreakpoint";
import { useEffect, useMemo } from "react";
import { shadows, sizes, transitions } from "ui/src/design-tokens";
import { AutoCompletePanel } from "../components/autocomplete-panel/AutoCompletePanel";
import { useSearchBar } from "./useSearchBar";
import type { SystemStyleObject } from "@ui/types";
import { Logo } from "../components/Logo";
import { Flex } from "@ui/components/layout/flex/Flex";
import { useRouteChange } from "@store/hooks/useRouteChange";

interface SearchBarProps extends BoxProps {
    searchBarOpen?: boolean;
    onClose?: () => void;
}

export const SearchBar = ({ searchBarOpen, onClose }: SearchBarProps) => {
    const { get: getMicrocopy } = useMicrocopy();
    const lg = useBreakpoint("lg");
    const isDesktopView = lg;

    const {
        autocomplete,
        recentSearchesPlugin,
        containerRef,
        formRef,
        inputRef,
        isLoading,
        setIsLoading,
        autocompleteState,
    } = useSearchBar({ searchBarOpen, isDesktopView });

    // Close search bar if user is navigating away
    useRouteChange(() => {
        setIsLoading(false);
        onClose?.();
    });

    // Focus input when search bar opens
    useEffect(() => {
        searchBarOpen && inputRef?.current && inputRef.current.focus();
    }, [inputRef, searchBarOpen]);

    const formProps = useMemo(
        () =>
            autocomplete?.getFormProps({
                inputElement: inputRef.current,
            }),
        [autocomplete, inputRef]
    );
    const inputProps = useMemo(
        () =>
            autocomplete?.getInputProps({
                inputElement: inputRef?.current,
                maxLength: 100,
                value: autocompleteState?.query,
            }),
        [autocomplete, inputRef, autocompleteState]
    );

    // Track search queries with the analytics client
    useEffect(() => {
        let analyticsTimeout;
        if (autocompleteState?.query) {
            analyticsTimeout = setTimeout(() => {
                AnalyticsClient.saveSearch(autocompleteState?.query);
            }, 300);
        }
        return () => clearTimeout(analyticsTimeout);
    }, [autocompleteState?.query]);

    const containerStyles: SystemStyleObject = useMemo(() => {
        const base = {
            position: {
                base: "fixed",
                lg: "absolute",
            },
            transformOrigin: "center",
            transitionProperty: "opacity",
            transitionDuration: transitions.duration.fast,
            transitionTimingFunction: transitions.easing["ease-out"],
            pointerEvents: "none",
            opacity: 0,
            zIndex: 0,
            left: 0,
            right: 0,
            top: 0,
            backgroundColor: "white",
            padding: {
                base: 4,
                lg: 0,
            },
            height: {
                base: "100dvh",
                lg: "85dvh",
            },
            width: {
                base: "100%",
                lg: "auto",
            },
        };
        if (searchBarOpen) {
            return {
                ...base,
                pointerEvents: "auto",
                zIndex: 1,
                opacity: 1,
            };
        }
        return base;
    }, [searchBarOpen]);

    // Handle closing the search bar with the Escape key
    useEffect(() => {
        if (!searchBarOpen) return;
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose?.();
            }
        };

        document.addEventListener("keydown", handleKeydown);
        return () => {
            document.removeEventListener("keydown", handleKeydown);
        };
    }, [onClose, searchBarOpen]);

    return (
        <Box
            className="aa-Autocomplete"
            ref={containerRef}
            {...autocomplete?.getRootProps()}
            sx={containerStyles}
            data-testid={SEARCH_OVERLAY}
        >
            <Flex
                as="form"
                ref={formRef}
                className="aa-Form"
                display="flex"
                // Chakra polymorphic component doesn't have proper type inference with the element used.
                // As a result the <Flex as="form" /> component expects to match a <div> element and not a <form> element.
                {...(formProps as any)}
                position="relative"
                boxShadow={{ base: "none", lg: shadows.fromBottom }}
            >
                <Flex
                    sx={{
                        ...logoContainerStyles,
                        display: {
                            base: "none",
                            lg: "flex",
                        },
                    }}
                >
                    <Logo />
                </Flex>
                <InputGroup
                    sx={{
                        opacity: searchBarOpen ? 1 : 0,
                    }}
                    className="aa-InputWrapper"
                    w="100%"
                    bg="white"
                >
                    <InputLeftElement
                        w={sizes.icon["4xs"]}
                        h={sizes.navBarCollapsedHeight}
                        zIndex={3}
                        position="absolute"
                        tabIndex={-1}
                    >
                        <ECCOIcon name="search" aria-label="Search" />
                    </InputLeftElement>
                    <Input
                        tabIndex={0}
                        className="aa-Input"
                        ref={inputRef}
                        aria-selected={searchBarOpen}
                        display={[
                            searchBarOpen ? "flex" : "none",
                            searchBarOpen ? "flex" : "none",
                            "flex",
                            "flex",
                        ]}
                        {...(inputProps as any)}
                        sx={searchInputStyles}
                        textOverflow="ellipsis"
                        cursor={searchBarOpen ? "text" : "pointer"}
                        whiteSpace="nowrap"
                        data-testid={SEARCHBAR_INPUT}
                    />
                </InputGroup>
                {searchBarOpen && (
                    <Box
                        width="10.4"
                        display="flex"
                        pointerEvents={searchBarOpen ? "auto" : "none"}
                        justifyContent="center"
                        zIndex={2}
                        position="absolute"
                        right={0}
                        top={0}
                    >
                        <IconButton
                            w="10.4"
                            height={{
                                base: sizes.navBarCollapsedHeight,
                                sm: sizes.navBarCollapsedHeight,
                            }}
                            zIndex={3}
                            aria-label={getMicrocopy(
                                CommonCMS.shared,
                                CommonCMS.ariaLabels.closeModalAria
                            )}
                            icon={<ECCOIcon name="close" />}
                            variant="solid"
                            onClick={() => {
                                onClose?.();
                            }}
                        />
                    </Box>
                )}
            </Flex>
            <AutoCompletePanel
                autocompleteState={autocompleteState}
                autocomplete={autocomplete}
                isLoading={isLoading}
                recentSearchesPlugin={recentSearchesPlugin}
                searchBarOpen={searchBarOpen}
            />
        </Box>
    );
};
